import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Popover, Portal, Box, Input, Text } from '@chakra-ui/react'
import { FaChevronDown, FaSearch, FaSort } from 'react-icons/fa'
import Icon from './Icon'

/**
 * Searchable dropdown with Chakra styling.
 * options: Array<
 *   | { value: string, label: string }
 *   | { type: 'divider', key?: string }
 * >
 * value: selected value (string)
 * onChange: (value: string) => void
 */
export default function SearchableSelect ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  size = 'md',
  maxW,
  w,
  bg = 'bg.subtle',
  variant = 'outline',
  ellipsize = false,
  action,
  emptyMessage = 'No options match',
  ...rest
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const selectedOption = useMemo(
    () => options.find(o => o.value === value),
    [options, value]
  )
  const displayLabel = selectedOption ? selectedOption.label : placeholder

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options
    const q = query.trim().toLowerCase()
    return options.filter(o => {
      if (o.type === 'divider') return true
      return (o.label || '').toLowerCase().includes(q)
    })
  }, [options, query])

  const isGhost = variant === 'ghost'
  const triggerBg = isGhost ? 'transparent' : bg
  const triggerBorderWidth = isGhost ? 0 : '1px'
  const triggerHover = isGhost
    ? { bg: 'bg.subtle' }
    : { borderColor: 'border.muted', bg: 'bg.surface' }
  const triggerFocus = isGhost
    ? { outline: 'none', borderColor: 'transparent', boxShadow: 'none' }
    : { outline: 'none', borderColor: 'border.muted', boxShadow: '0 0 0 2px var(--chakra-colors-border-muted)' }

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) {
      setQuery('')
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  function handleSelect (option) {
    onChange(option.value)
    setOpen(false)
  }

  function handleKeyDown (e) {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const items = listRef.current?.querySelectorAll('[data-searchable-option]')
      if (!items?.length) return
      const current = document.activeElement
      const idx = Array.from(items).findIndex(el => el === current)
      let next = idx + (e.key === 'ArrowDown' ? 1 : -1)
      if (next < 0) next = items.length - 1
      if (next >= items.length) next = 0
      items[next]?.focus()
    }
    if (e.key === 'Enter') {
      const items = listRef.current?.querySelectorAll('[data-searchable-option]')
      const focused = Array.from(items || []).find(el => el === document.activeElement)
      if (focused?.dataset?.value) {
        handleSelect({ value: focused.dataset.value, label: focused.textContent })
      }
    }
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={e => setOpen(e.open)}
      positioning={{ sameWidth: true }}
    >
      <Popover.Trigger asChild>
        <Box
          {...rest}
          as='button'
          type='button'
          w={w}
          maxW={maxW}
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          gap={2}
          px={size === 'sm' ? 2 : 3}
          py={size === 'sm' ? 1 : 2}
          minH={size === 'sm' ? '32px' : '40px'}
          bg={triggerBg}
          borderRadius='md'
          borderWidth={triggerBorderWidth}
          borderColor='border.default'
          cursor='pointer'
          textAlign='left'
          fontSize={size === 'sm' ? 'sm' : 'md'}
          _hover={triggerHover}
          _focus={triggerFocus}
        >
          <Text
            lineClamp={ellipsize ? 1 : undefined}
            flex={1}
            color={selectedOption ? 'inherit' : 'fg.muted'}
            fontWeight={isGhost ? '600' : 'normal'}
          >
            {displayLabel}
          </Text>
          <Icon opacity={0.6}>
            {isGhost ? <FaSort /> : <FaChevronDown />}
          </Icon>
        </Box>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            p={0}
            maxH='320px'
            overflow='hidden'
            display='flex'
            flexDirection='column'
            boxShadow='lg'
            borderRadius='md'
            borderWidth='1px'
          >
            <Box p={2} borderBottomWidth='1px' borderColor='border.muted'>
              <Box position='relative'>
                <Icon
                  position='absolute'
                  left={2}
                  top='50%'
                  transform='translateY(-50%)'
                  opacity={0.5}
                  fontSize='sm'
                >
                  <FaSearch />
                </Icon>
                <Input
                  ref={inputRef}
                  size='sm'
                  pl={8}
                  pr={2}
                  placeholder='Type to search...'
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  borderColor='border.muted'
                  _focus={{ borderColor: 'border.muted' }}
                />
              </Box>
            </Box>
            <Box ref={listRef} overflowY='auto' flex={1} minH={0} py={1}>
              {filteredOptions.length === 0
                ? (
                  <Text px={3} py={4} color='fg.muted' fontSize='sm'>
                    {emptyMessage}
                  </Text>
                  )
                : (
                    filteredOptions.map((option, index) => {
                      if (option.type === 'divider') {
                        return (
                          <Box
                            key={option.key || `divider-${index}`}
                            borderTopWidth='1px'
                            borderColor='border.muted'
                            my={1}
                          />
                        )
                      }

                      return (
                        <Box
                          key={option.value}
                          data-searchable-option
                          data-value={option.value}
                          tabIndex={0}
                          px={3}
                          py={2}
                          cursor='pointer'
                          fontSize='sm'
                          _hover={{ bg: 'bg.muted' }}
                          _focus={{ bg: 'bg.muted', outline: 'none' }}
                          bg={option.value === value ? 'bg.muted' : 'transparent'}
                          color={option.value === value ? 'fg.default' : 'inherit'}
                          fontWeight={option.value === value ? '600' : 'normal'}
                          onKeyDown={handleKeyDown}
                          onClick={() => handleSelect(option)}
                        >
                          {option.label}
                        </Box>
                      )
                    })
                  )}
            </Box>
            {action && (
              <Box borderTopWidth='1px' borderColor='border.muted' p={2}>
                {action}
              </Box>
            )}
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}
